import { GemSalesConfig, ShopperContext } from "./types";
import { getRecommendationAttribution } from "./storage";

interface BehaviorTracker {
  track: (eventName: string, properties?: Record<string, unknown>) => void;
}

export function observeBehavior(config: GemSalesConfig, context: ShopperContext, tracker: BehaviorTracker): () => void {
  const cleanup: Array<() => void> = [];

  cleanup.push(observeScrollDepth(tracker));
  cleanup.push(observeCtaClicks(tracker));
  cleanup.push(observeFaqOpen(tracker));
  cleanup.push(observeSearchFocus(tracker));
  cleanup.push(observeCartIdle(config, context, tracker));

  return () => {
    cleanup.forEach((stop) => stop());
  };
}

function observeScrollDepth(tracker: BehaviorTracker): () => void {
  const milestones = [25, 50, 75, 90];
  const seen = new Set<number>();
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;

    window.requestAnimationFrame(() => {
      ticking = false;
      const depth = getScrollDepth();
      const milestone = milestones.find((candidate) => depth >= candidate && !seen.has(candidate));
      if (!milestone) return;

      seen.add(milestone);
      tracker.track("behavior.scroll_depth", {
        depth_percent: milestone,
      });
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  return () => window.removeEventListener("scroll", onScroll);
}

function observeCtaClicks(tracker: BehaviorTracker): () => void {
  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    const clickable = target?.closest<HTMLElement>("button, a, input[type='submit']");
    if (!clickable || !isCommerceCta(clickable)) return;

    tracker.track("behavior.cta_clicked", {
      cta_text: normalizeText(clickable.textContent ?? clickable.getAttribute("value") ?? ""),
      cta_href: clickable instanceof HTMLAnchorElement ? clickable.href : null,
      cta_action: clickable.closest("form")?.getAttribute("action") ?? null,
    });

    if (!isAddToCartCta(clickable)) return;

    const attribution = getRecommendationAttribution();
    if (!attribution) return;

    tracker.track("gemsales.add_to_cart.assist", {
      recommendation_id: attribution.recommendationId,
      flow_id: attribution.flowId,
      result_title: attribution.resultTitle,
      cta_url: attribution.ctaUrl,
      recommended_product_handle: attribution.recommendedProductHandle,
      attribution_window_minutes: 30,
    });
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

function observeFaqOpen(tracker: BehaviorTracker): () => void {
  const onToggle = (event: Event) => {
    const details = event.target instanceof HTMLDetailsElement ? event.target : null;
    if (!details?.open) return;

    tracker.track("behavior.faq_opened", {
      question_text: normalizeText(details.querySelector("summary")?.textContent ?? ""),
      faq_intent: classifyFaqIntent(details.textContent ?? ""),
    });
  };

  document.addEventListener("toggle", onToggle, true);
  return () => document.removeEventListener("toggle", onToggle, true);
}

function observeSearchFocus(tracker: BehaviorTracker): () => void {
  const seen = new WeakSet<Element>();

  const onFocus = (event: FocusEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || seen.has(target) || !isSearchInput(target)) return;

    seen.add(target);
    tracker.track("behavior.search_focused", {
      input_name: target.getAttribute("name"),
      input_id: target.id || null,
    });
  };

  document.addEventListener("focusin", onFocus);
  return () => document.removeEventListener("focusin", onFocus);
}

function observeCartIdle(config: GemSalesConfig, context: ShopperContext, tracker: BehaviorTracker): () => void {
  const subtotal = context.page.cartSubtotal;
  if (subtotal === null || subtotal >= config.commerce.freeShippingThreshold) {
    return () => undefined;
  }

  const idleMs = 10_000;
  const timer = window.setTimeout(() => {
    tracker.track("behavior.cart_idle", {
      cart_value: subtotal,
      free_shipping_threshold: config.commerce.freeShippingThreshold,
      free_shipping_gap: Math.max(config.commerce.freeShippingThreshold - subtotal, 0),
      idle_ms: idleMs,
    });
  }, idleMs);

  return () => window.clearTimeout(timer);
}

function getScrollDepth(): number {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
  const scrollHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight,
    document.documentElement.clientHeight,
    1,
  );
  const maxScrollable = Math.max(scrollHeight - viewportHeight, 1);
  return Math.min(100, Math.round((scrollTop / maxScrollable) * 100));
}

function isCommerceCta(element: HTMLElement): boolean {
  const text = normalizeText(element.textContent ?? element.getAttribute("value") ?? "").toLowerCase();
  const href = element instanceof HTMLAnchorElement ? element.href.toLowerCase() : "";
  const formAction = element.closest("form")?.getAttribute("action")?.toLowerCase() ?? "";

  return (
    text.includes("add to cart") ||
    text.includes("buy now") ||
    text.includes("checkout") ||
    href.includes("/cart") ||
    href.includes("/checkout") ||
    formAction.includes("/cart/add")
  );
}

function isAddToCartCta(element: HTMLElement): boolean {
  const text = normalizeText(element.textContent ?? element.getAttribute("value") ?? "").toLowerCase();
  const formAction = element.closest("form")?.getAttribute("action")?.toLowerCase() ?? "";
  return text.includes("add to cart") || formAction.includes("/cart/add");
}

function isSearchInput(element: Element): boolean {
  if (!(element instanceof HTMLInputElement)) {
    return element.getAttribute("role") === "searchbox";
  }

  const type = element.getAttribute("type")?.toLowerCase();
  const name = element.getAttribute("name")?.toLowerCase();
  return type === "search" || name === "q" || element.getAttribute("role") === "searchbox";
}

function classifyFaqIntent(text: string): string {
  const normalized = text.toLowerCase();
  if (normalized.includes("medication") || normalized.includes("condition") || normalized.includes("pregnant")) {
    return "medication_condition";
  }
  if (normalized.includes("taste") || normalized.includes("flavor")) return "taste";
  if (normalized.includes("lab") || normalized.includes("tested") || normalized.includes("proof")) return "lab_proof";
  if (normalized.includes("shipping") || normalized.includes("return") || normalized.includes("guarantee")) {
    return "shipping_returns";
  }
  return "unresolved_objection";
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 140);
}
